import { FC, useCallback, useState } from 'react'

import { flip, offset, useFloating } from '@floating-ui/react-dom'
import { Editor } from '@tiptap/react'
import { BaseEmoji, Picker } from 'emoji-mart-virtualized'
import EmotionLineIcon from 'remixicon-react/EmotionLineIcon'

import { ButtonSize } from '@tribeplatform/react-ui-kit/Button'
import { Portal } from '@tribeplatform/react-ui-kit/Portal'

import { useOnClickOutside } from '../hooks/useOnClickOutside'
import { ComposerIconButton } from './ComposerIconButton'

interface EmojiPickerButtonProps {
  editor: Editor
  size?: ButtonSize
}

export const EmojiPickerButton: FC<EmojiPickerButtonProps> = ({
  editor,
  size,
}) => {
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false)

  const { x, y, reference, floating, strategy, refs } = useFloating({
    placement: 'top-start',
    middleware: [offset(4), flip()],
  })

  useOnClickOutside(refs.floating, e => {
    if (!refs.floating.current?.contains(e.target)) {
      setIsEmojiPickerOpen(false)
    }
  })

  const onSelectEmoji = useCallback(
    (emoji: BaseEmoji) => {
      const position = {
        from: editor.state.selection.from,
        to: editor.state.selection.to,
      }

      editor
        .chain()
        .focus()
        .insertContentAt(position, [
          {
            type: 'text',
            text: emoji.native,
          },
          {
            type: 'text',
            text: ' ',
          },
        ])
        .run()

      setIsEmojiPickerOpen(false)
    },
    [editor],
  )

  return (
    <div>
      <ComposerIconButton
        onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
        icon={EmotionLineIcon}
        aria-label="Emoji"
        size={size}
        ref={reference}
      />
      <Portal>
        {isEmojiPickerOpen && (
          <div
            ref={floating}
            style={{
              position: strategy,
              top: '0',
              left: '0',
              transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
            }}
          >
            <Picker
              onSelect={onSelectEmoji}
              showSkinTones={false}
              showPreview={false}
              native
            />
          </div>
        )}
      </Portal>
    </div>
  )
}
